import React from 'react';
import Button from '@cobalt/react-button';
import Grid, { Column } from '@cobalt/react-grid';
import { Text } from '@cobalt/react-typography';
import Box from '@cobalt/react-box';
import Divider from '@cobalt/react-divider';
import Flex from '@cobalt/react-flex';
import Icon from '@cobalt/react-icon';
import Dropdown, { Option } from '@cobalt-marketplace/react-dropdown';

const MonthSelection = ({
  months,
  onClickMonth,
  selectedMonth
}) => {
  return /*#__PURE__*/React.createElement(Grid, {
    columns: "3",
    gap: "1",
    padding: "3"
  }, Object.values(months).map((month, monthKey) => {
    const isMonthSelected = selectedMonth === monthKey + 1;
    return /*#__PURE__*/React.createElement(Column, {
      key: month
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: () => onClickMonth(monthKey + 1),
      shape: "full-width",
      size: "small",
      type: isMonthSelected ? "primary" : "secondary",
      variation: isMonthSelected ? undefined : "transparent"
    }, /*#__PURE__*/React.createElement(Text, {
      inline: true
    }, month)));
  }));
};

const MonthYearNavigation = ({
  availableYears,
  month,
  months,
  onChangeMonth,
  onChangeYear,
  onClickMonth,
  year
}) => {
  const previousMonthDisabled = month === 1 && year === availableYears[0];
  const nextMonthDisabled = month === 12 && year === availableYears[availableYears.length - 1];

  const onClickPreviousMonth = () => {
    if (month === 1) {
      onChangeMonth(12);
      onChangeYear(availableYears[availableYears.indexOf(year) - 1]);
      return;
    }

    onChangeMonth(month - 1);
  };

  const onClickNextMonth = () => {
    if (month === 12) {
      onChangeMonth(1);
      onChangeYear(availableYears[availableYears.indexOf(year) + 1]);
      return;
    }

    onChangeMonth(month + 1);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    alignY: "center",
    paddingX: "3",
    paddingY: "1"
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: previousMonthDisabled,
    onClick: onClickPreviousMonth,
    shape: "compact",
    size: "small",
    type: "secondary",
    variation: "transparent"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "chevron_left",
    size: "small"
  })), /*#__PURE__*/React.createElement(Box, {
    paddingX: "1",
    style: {
      overflow: "hidden"
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClickMonth,
    shape: "full-width",
    size: "small",
    type: "secondary",
    variation: "transparent"
  }, /*#__PURE__*/React.createElement(Text, {
    inline: true,
    truncated: true
  }, months[month]))), /*#__PURE__*/React.createElement(Button, {
    disabled: nextMonthDisabled,
    onClick: onClickNextMonth,
    shape: "compact",
    size: "small",
    type: "secondary",
    variation: "transparent"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "chevron_right",
    size: "small"
  })), /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "1",
    style: {
      minWidth: "42%"
    }
  }, /*#__PURE__*/React.createElement(Dropdown, {
    inputSize: "small",
    onChange: ({
      value
    }) => onChangeYear(value),
    value: year
  }, availableYears.map(availableYear => /*#__PURE__*/React.createElement(Option, {
    key: availableYear,
    selected: year === availableYear,
    title: availableYear,
    value: availableYear
  }))))), /*#__PURE__*/React.createElement(Divider, null));
};

const defaultMonths = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December"
};
const defaultWeekdays = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun"
};

const getDateFromDayObject = date => new Date(`${date.year}-${date.month}-${date.day}`);

const checkDaysRangeInMonthYear = (date, currentMonth, currentYear) => {
  const startDate = date.start;
  const endDate = date.end;

  if (endDate && startDate && getDateFromDayObject(endDate).getTime() < getDateFromDayObject(startDate).getTime()) {
    return;
  }

  if (startDate || endDate) {
    const tempDate = {};
    const currentTime = new Date(`${currentYear}-${currentMonth}`).getTime(); // We want to consider not only the month and year that startDate occurs but all the time until it reaches endDate date and month or forever if there is no endDate

    if (startDate && new Date(`${startDate.year}-${startDate.month}`).getTime() <= currentTime) {
      if (startDate.year === currentYear && startDate.month === currentMonth) {
        tempDate.start = startDate.day;
      } else if (!endDate || currentTime < getDateFromDayObject(endDate).getTime()) {
        // In case we don't have endDate or the time is before it we consider the startDate from the day 1
        tempDate.start = 1;
      }
    } // We want to consider not only the month and year of that endDate occurs but all the time before it if there is no startDate


    if (endDate && getDateFromDayObject(endDate).getTime() >= currentTime) {
      if (endDate.year === currentYear && endDate.month === currentMonth) {
        tempDate.end = endDate.day;
      } else if (!startDate) {
        // In the case that we are before the endDate year and month we set the start instead because months have different length in days
        tempDate.start = 1;
      }
    }

    if (tempDate.end || tempDate.start) {
      return tempDate;
    }
  }
};

const checkDaysInMonthYear = (date, currentMonth, currentYear) => {
  if (date.year === currentYear && date.month === currentMonth) {
    return date.day;
  }
};

const formatDaysForCalendar = (dates, currentMonth, currentYear) => {
  return dates.reduce((prev, date) => {
    let days;

    if (date.start || date.end) {
      days = checkDaysRangeInMonthYear(date, currentMonth, currentYear);
    } else {
      days = checkDaysInMonthYear(date, currentMonth, currentYear);
    }

    if (days) {
      prev.push(days);
    }

    return prev;
  }, []);
};
const getDefaultMonth = (initialMonth, selectedDay) => {
  if (initialMonth) {
    return initialMonth;
  }

  if (selectedDay) {
    return selectedDay.month;
  }

  return new Date().getMonth() + 1;
};
const getDefaultYear = (initialYear, selectedDay) => {
  if (initialYear) {
    return initialYear;
  }

  if (selectedDay) {
    return selectedDay.year;
  }

  return new Date().getFullYear();
};

export { MonthSelection, MonthYearNavigation, defaultMonths, defaultWeekdays, formatDaysForCalendar, getDateFromDayObject, getDefaultMonth, getDefaultYear };
